import Header from '../components/Header';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import { fetchOrders } from '../store/purchaseSlice';
import { useEffect, useRef, useState } from 'react';
import { formatCurrency } from '../utils';
import { useNavigate } from 'react-router-dom';
import BackNavBar from '../components/BackNavBar';
import ReceiptCard from '../components/ReceiptCard';
import purchaseService from '../services/purchaseService';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import DownloadIcon from '/asset/download.svg';
import Popup from '../components/Popup';
import { Order, PaymentMethod } from '../interfaces';
import { openFrame } from '../utils/iframeModal';
import { fetchUserInfo } from '../store/authSlice';

const orderStatusStyle = (status: string): string => {
  const s = String(status).toLowerCase();
  if (s.includes('delivered') || s.includes('completed'))
    return 'text-green-600 bg-green-50';
  if (s.includes('pending') || s.includes('processing'))
    return 'text-amber-600 bg-amber-50';
  if (s.includes('cancel')) return 'text-red-500 bg-red-50';
  return 'text-gray-600 bg-gray-100';
};

export default function History() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { miscellanousFeeName } = useSelector(
    (state: RootState) => state.purchase
  );
  const orders = useSelector((state: RootState) => state.purchase.orders);
  const { routesPrefix } = useSelector((state: RootState) => state.setup);
  const { storeInfo } = useSelector((state: RootState) => state.products);
  const user = useSelector((state: RootState) => state.auth.user);
  const bnplLink = import.meta.env.VITE_BNPL_URL as string;
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [downloadingCode, setDownloadingCode] = useState<string | null>(null);
  const [downloadOrderDetails, setDownloadOrderDetails] = useState<Awaited<
    ReturnType<typeof purchaseService.getSingleOrderByPurchaseCode>
  > | null>(null);
  const downloadRef = useRef<HTMLDivElement | null>(null);

  const [activeBnplOrder, setActiveBnplOrder] = useState<Order | null>(null);
  const [showBnplOptionsModal, setShowBnplOptionsModal] =
    useState<boolean>(false);

  useEffect(() => {
    dispatch(fetchUserInfo());
    dispatch(fetchOrders());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!downloadOrderDetails) return;

    const generatePDF = async () => {
      const element = downloadRef.current;
      if (!element) return;
      try {
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
        });
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 210;
        const pxPerMm = canvas.width / imgWidth;
        const imgHeight = canvas.height / pxPerMm;
        const pdf = new jsPDF({
          orientation: 'p',
          unit: 'mm',
          format: [imgWidth, imgHeight],
        });
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        pdf.save(`receipt-${downloadOrderDetails.purchaseCode}.pdf`);
      } finally {
        setDownloadOrderDetails(null);
        setDownloadingCode(null);
      }
    };

    generatePDF();
  }, [downloadOrderDetails]);

  const toggle = (index: number) =>
    setExpandedIndex((prev) => (prev === index ? null : index));

  const handleDownloadReceipt = async (purchaseCode: string) => {
    if (downloadingCode) return;
    setDownloadingCode(purchaseCode);
    try {
      const res =
        await purchaseService.getSingleOrderByPurchaseCode(purchaseCode);
      if (res?.purchaseCode) {
        setDownloadOrderDetails(res);
      } else {
        setDownloadingCode(null);
      }
    } catch {
      setDownloadingCode(null);
    }
  };

  const handleContinueBnpl = (order: Order) => {
    setActiveBnplOrder(order);
    setShowBnplOptionsModal(true);
  };

  const selectBnplOption = (paymentMethod: PaymentMethod) => {
    if (!activeBnplOrder) return;
    const order = activeBnplOrder;

    const params: Record<string, string> = {
      amount: String(order.totalAmountWithVAT),
      purchaseCode: order.purchaseCode,
      phoneNumber: user?.phoneNumber || '',
    };
    if (user?.email) {
      params.email = user.email;
    }
    if (user?.firstName) {
      params.firstName = user.firstName;
    }
    if (user?.lastName) {
      params.lastName = user.lastName;
    }

    const bnplCheckoutUrl = `${bnplLink}${paymentMethod?.bnplShortCode}?${new URLSearchParams(
      params
    ).toString()}`;
    setShowBnplOptionsModal(false);
    openFrame(bnplCheckoutUrl);
  };

  const renderBnplOption = (paymentMethod: PaymentMethod, index: number) => {
    const BNPLTEXT =
      paymentMethod?.checkoutButtonText &&
      paymentMethod?.checkoutButtonText !== ''
        ? paymentMethod?.checkoutButtonText
        : `Pay Small Small (${index + 1})`;
    return (
      <div
        key={`paymentmethod-${index}`}
        role="button"
        tabIndex={0}
        aria-label={`Select ${paymentMethod?.name}`}
        className="
          w-full flex items-center gap-[14px]
        border
        border-primary-200
        hover:!bg-blue-50
        hover:border-secondary-400
        rounded-md
        px-[16px] py-[14px] cursor-pointer
        transition-colors"
        style={{
          backgroundColor: paymentMethod?.checkoutButtonSecondaryColour
            ? `${paymentMethod?.checkoutButtonSecondaryColour}10`
            : undefined,
        }}
        onClick={() => selectBnplOption(paymentMethod)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            selectBnplOption(paymentMethod);
          }
        }}
      >
        <div className="flex-shrink-0 w-[36px] h-[36px] flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 30 30"
            fill="none"
            stroke={paymentMethod?.checkoutButtonPrimaryColour || '#2563eb'}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-[28px] h-[28px]"
          >
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
            <path d="M12 6v6l4 2" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-[14px] font-semibold leading-tight"
            style={{
              color: paymentMethod?.checkoutButtonPrimaryColour || '#2563eb',
            }}
          >
            {paymentMethod?.name}
          </p>
          <p className="text-[12px] text-gray-500 mt-[2px]">{BNPLTEXT}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <Popup
        isOpen={showBnplOptionsModal}
        popUpClass="!max-w-[420px] !w-[90%]"
        onClose={() => {
          setShowBnplOptionsModal(false);
          setActiveBnplOrder(null);
        }}
      >
        <div className="p-[10px]">
          <p className="text-gray-500 font-dmsans text-[12px] mb-[16px]">
            Choose a Pay Small Small option to continue this order
          </p>
          <div className="flex flex-col gap-[10px]">
            {storeInfo?.paymentMethods?.map((paymentMethod, index) =>
              renderBnplOption(paymentMethod, index)
            )}
          </div>
        </div>
      </Popup>

      <BackNavBar
        label={'Back to Storefront'}
        onClick={() => {
          navigate(`${routesPrefix}`);
        }}
      />
      <div className="mx-auto bg-secondary-50 min-h-screen">
        <div className="px-4 py-6 max-w-[560px] m-auto">
          <h1 className="font-bold text-[18px] font-dmsans text-gray-900 mb-5">
            Order History
          </h1>

          {orders !== null && orders.length === 0 && (
            <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
              <p className="text-[13px] text-gray-400 font-dmsans">
                No orders yet.
              </p>
            </div>
          )}

          <div className="space-y-3">
            {orders?.map((thisOrder, index) => {
              if (typeof thisOrder.purchaseCode === 'undefined') return null;
              const isExpanded = expandedIndex === index;

              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-sm overflow-hidden"
                >
                  {/* Card header */}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <span
                        className={`text-[11px] font-semibold font-dmsans py-1 rounded-full ${orderStatusStyle(thisOrder.orderStatus)}`}
                      >
                        {thisOrder.orderStatus}
                        {thisOrder.cartStatus &&
                          thisOrder.cartStatus === 'BNPLCheckoutInitiated' && (
                            <span> (Pay Small Small Initiated)</span>
                          )}
                      </span>

                      {thisOrder.cartStatus &&
                      thisOrder.cartStatus === 'BNPLCheckoutInitiated' ? (
                        <button
                          onClick={() => handleContinueBnpl(thisOrder)}
                          className="
                          bg-blue-200
                          text-[12px]
                          font-dmsans
                           py-1 px-3
                          text-secondary-500
                          "
                        >
                          Continue Pay Small Small
                        </button>
                      ) : (
                        <button
                          disabled={downloadingCode === thisOrder.purchaseCode}
                          onClick={() =>
                            handleDownloadReceipt(thisOrder.purchaseCode)
                          }
                          className="
                        bg-white
                        text-[12px]
                        font-dmsans
                        p-0
                        text-secondary-500
                        !border-none
                        hover:text-secondary-500
                        hover:border-none
                        hover:outline-none
                        focus:outline-none
                        focus:border-none
                        font-medium text-gray-400
                        whitespace-nowrap
                        flex items-center gap-1
                        disabled:opacity-50
                        disabled:cursor-not-allowed"
                        >
                          <img
                            src={DownloadIcon}
                            className="w-[20px] h-[20px] object-contain"
                            alt=""
                          />
                          {downloadingCode === thisOrder.purchaseCode
                            ? 'Downloading...'
                            : 'Download Receipt'}
                        </button>
                      )}
                    </div>

                    {/* Amount */}
                    <div className="mb-3">
                      <div className="text-black text-[10px]">
                        {/* {thisOrder.createdAt.slice(0, 10)} */}
                      </div>
                      <span className="text-[22px] font-bold text-gray-900 font-dmsans">
                        {formatCurrency(thisOrder.totalAmountWithVAT)}
                      </span>
                    </div>

                    <hr className="border-gray-100 mb-3" />

                    {/* Meta row */}
                    <div className="flex items-start divide-x divide-gray-200">
                      <div className="pr-4">
                        <p className="text-[10px] text-gray-400 font-dmsans mb-0.5">
                          Order No
                        </p>
                        <p className="text-[12px] font-medium text-gray-900 font-dmsans">
                          {thisOrder.purchaseCode}
                        </p>
                      </div>
                      <div className="px-4">
                        <p className="text-[10px] text-gray-400 font-dmsans mb-0.5">
                          Order Type
                        </p>
                        <p className="text-[12px] font-medium text-gray-900 font-dmsans">
                          {thisOrder.orderType === 0 ||
                          thisOrder.orderType === 'Pickup'
                            ? 'Pickup'
                            : 'Delivery'}
                        </p>
                      </div>
                      {thisOrder?.paymentMode === 'BNPL' && (
                        <div className="px-4">
                          <p className="text-[10px] text-gray-400 font-dmsans mb-0.5">
                            Payment Mode
                          </p>
                          <p className="text-[12px] font-medium text-gray-900 font-dmsans">
                            Pay Small Small
                          </p>
                        </div>
                      )}
                      {(thisOrder.orderType === 1 ||
                        thisOrder.orderType === 'Delivery') &&
                        thisOrder.deliveryAddress && (
                          <div className="pl-4 flex-1 min-w-0">
                            <p className="text-[10px] text-gray-400 font-dmsans mb-0.5">
                              Delivery Address
                            </p>
                            <p className="text-[12px] font-medium text-gray-900 font-dmsans truncate">
                              {thisOrder.deliveryAddress}
                            </p>
                          </div>
                        )}
                    </div>

                    {/* Toggle */}
                    <button
                      onClick={() => toggle(index)}
                      className="
                        p-0
                        mt-5
                        bg-white
                        text-[12px] 
                        font-dmsans 
                        !border-none
                        hover:text-secondary-500
                        hover:border-none
                        hover:outline-none
                        focus:outline-none
                        focus:border-none
                        font-medium text-gray-400
                        whitespace-nowrap"
                    >
                      <span className="flex items-center gap-1 text-secondary-500">
                        {isExpanded ? 'Hide details' : 'Show details'}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </span>
                    </button>
                  </div>

                  {/* Expanded items */}
                  {isExpanded && (
                    <div className="px-5 pb-5 border-t border-gray-100">
                      <table className="w-full font-dmsans mt-4">
                        <thead>
                          <tr>
                            <th className="text-left text-[12px] font-normal text-gray-400 pb-2">
                              Item
                            </th>
                            <th className="text-center text-[12px] font-normal text-gray-400 pb-2">
                              Qty
                            </th>
                            <th className="text-right text-[12px] font-normal text-gray-400 pb-2">
                              Price
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {thisOrder.purchases.map((item, i) => (
                            <tr key={i} className="border-t border-gray-100">
                              <td className="py-2.5">
                                <div className="flex items-center gap-2.5">
                                  <img
                                    src={item.image}
                                    alt={item.name}
                                    className="w-8 h-8 rounded-md object-cover flex-shrink-0"
                                  />
                                  <div className="flex flex-col">
                                    <span className="text-[13px] text-gray-900">
                                      {item.name}
                                    </span>
                                    {item.sku && (
                                      <span className="text-[11px] text-gray-500">
                                        SKU: {item.sku}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="py-2.5 text-[13px] text-gray-900 text-center">
                                {item.quantity}
                              </td>
                              <td className="py-2.5 text-[13px] text-gray-900 text-right">
                                {formatCurrency(
                                  (item.unitPrice || 0) * (item.quantity ?? 1)
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Summary box */}
                      <div className="mt-4 bg-gray-50 rounded-xl p-4 font-dmsans">
                        <div className="space-y-2">
                          {(thisOrder.totalAmount ?? 0) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-[13px] text-gray-500">
                                Subtotal
                              </span>
                              <span className="text-[13px] text-gray-900">
                                {formatCurrency(thisOrder.totalAmount)}
                              </span>
                            </div>
                          )}
                          {(thisOrder.vat ?? 0) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-[13px] text-gray-500">
                                VAT
                              </span>
                              <span className="text-[13px] text-gray-900">
                                {formatCurrency(thisOrder.vat)}
                              </span>
                            </div>
                          )}
                          {(thisOrder.consumptionFee ?? 0) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-[13px] text-gray-500">
                                Consumption Fee
                              </span>
                              <span className="text-[13px] text-gray-900">
                                {formatCurrency(thisOrder.consumptionFee)}
                              </span>
                            </div>
                          )}
                          {(thisOrder.processingFee ?? 0) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-[13px] text-gray-500">
                                Processing Fee
                              </span>
                              <span className="text-[13px] text-gray-900">
                                {formatCurrency(thisOrder.processingFee ?? 0)}
                              </span>
                            </div>
                          )}
                          {(thisOrder.miscellaneousFee ?? 0) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-[13px] text-gray-500">
                                {miscellanousFeeName || 'Miscellanous Fee'}
                              </span>
                              <span className="text-[13px] text-gray-900">
                                {formatCurrency(
                                  thisOrder.miscellaneousFee ?? 0
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                        <hr className="border-gray-200 my-3" />
                        <div className="flex justify-between items-center">
                          <span className="text-[14px] font-semibold text-gray-900">
                            Total
                          </span>
                          <span className="text-[18px] font-bold text-gray-900">
                            {formatCurrency(thisOrder.totalAmountWithVAT)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {downloadOrderDetails && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            opacity: 0,
            pointerEvents: 'none',
            zIndex: -1,
          }}
        >
          <ReceiptCard ref={downloadRef} orderDetails={downloadOrderDetails} />
        </div>
      )}
    </div>
  );
}
